import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UiIcon from '../../components/common/UiIcon';
import CheckoutComplianceNotice from '../../components/common/CheckoutComplianceNotice';
import { getAuthSession, startGoogleLogin, type AuthUser } from '../../lib/auth';
import { trackEvent } from '../../lib/analytics';
import { getWalletSummary, redeemCouponCode, spendWalletForReading, startKakaoPayReady } from '../../lib/api';
import type { WalletSummaryResponse } from '../../lib/types';
import {
  clearWalletRechargeIntent,
  getSelectionRoute,
  getSpreadLabel,
  persistReadingSelection,
  readWalletRechargeIntent,
} from '../../lib/walletFlow';

const PENDING_READING_KEY = 'ny_pending_payment_reading';
const KAKAOPAY_PENDING_KEY = 'ny_kakaopay_pending_checkout';

type PaymentMode = 'disabled' | 'mock' | 'live';
type PaymentProvider = 'kakaopay';

interface PaymentConfigResponse {
  paymentMode: PaymentMode;
  provider: PaymentProvider;
  providerStatus: 'pending' | 'ready';
  isConfigured: boolean;
}

interface PendingKakaoPayState {
  orderType: 'wallet-recharge';
  packageId: string;
  readingId?: string;
  orderId: string;
  tid: string;
  amount: number;
  spread?: 'one-card' | 'three-card' | 'celtic-cross';
}

const EMPTY_FREE_READINGS = {
  'one-card': 0,
  'three-card': 0,
  'celtic-cross': 0,
} as const;

export default function WalletRechargePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intent = useMemo(() => readWalletRechargeIntent(), []);
  const recharged = searchParams.get('recharged') === '1';
  const rechargedPackageId = searchParams.get('packageId') || '';

  const [user, setUser] = useState<AuthUser | null>(null);
  const [wallet, setWallet] = useState<WalletSummaryResponse | null>(null);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [notice, setNotice] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isRecharging, setIsRecharging] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [isRedeemingCoupon, setIsRedeemingCoupon] = useState(false);

  useEffect(() => {
    trackEvent('wallet_recharge_opened', {
      source: intent ? intent.type : 'direct',
      spread: intent?.spread ?? '',
      required_points: intent?.requiredPoints ?? 0,
    });
  }, [intent]);

  useEffect(() => {
    let cancelled = false;

    const loadWalletContext = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const [session, configResponse] = await Promise.all([
          getAuthSession(),
          fetch('/api/payment/config', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          }),
        ]);

        if (cancelled) return;

        if (configResponse.ok) {
          const config = await configResponse.json() as PaymentConfigResponse;
          setPaymentConfig(config);
        }

        const nextUser = session?.user ?? null;
        setUser(nextUser);

        if (!nextUser) {
          setWallet(null);
          return;
        }

        const summary = await getWalletSummary();
        if (cancelled) return;
        setWallet(summary);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load wallet recharge page:', error);
          setErrorMessage('츄르 지갑 정보를 불러오지 못했습니다. 잠시 후 다시 확인해 주세요.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadWalletContext();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!wallet?.packages?.length) return;

    if (
      rechargedPackageId
      && wallet.packages.some((entry) => entry.id === rechargedPackageId)
      && rechargedPackageId !== selectedPackageId
    ) {
      setSelectedPackageId(rechargedPackageId);
      return;
    }

    if (selectedPackageId && wallet.packages.some((entry) => entry.id === selectedPackageId)) {
      return;
    }

    const recommended =
      wallet.packages.find((entry) => entry.recommended) ??
      wallet.packages.find((entry) => (intent?.requiredPoints ?? 0) <= entry.points) ??
      wallet.packages[0];

    if (recommended) {
      setSelectedPackageId(recommended.id);
    }
  }, [intent?.requiredPoints, selectedPackageId, wallet?.packages, rechargedPackageId]);

  const selectedPackage = wallet?.packages.find((entry) => entry.id === selectedPackageId) ?? null;
  const balancePoints = wallet?.balancePoints ?? 0;
  const freeReadings = wallet?.freeReadings ?? EMPTY_FREE_READINGS;
  const projectedBalance = balancePoints + (selectedPackage?.points ?? 0);
  const hasIntentCoupon = Boolean(intent && freeReadings[intent.spread] > 0);
  const canContinueWithIntent = Boolean(intent && (balancePoints >= intent.requiredPoints || hasIntentCoupon));
  const paymentMode = paymentConfig?.paymentMode ?? 'disabled';
  const provider = paymentConfig?.provider ?? 'kakaopay';
  const providerReady = paymentConfig?.providerStatus === 'ready';
  const isMockKakaoFlow = paymentMode === 'mock' && provider === 'kakaopay';
  const canStartKakaoRecharge =
    provider === 'kakaopay' && paymentMode !== 'disabled' && (isMockKakaoFlow || providerReady);

  const handleLogin = () => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    trackEvent('login_started', { source: 'wallet_recharge', return_to: returnTo });
    startGoogleLogin(returnTo);
  };

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim() || isRedeemingCoupon) return;

    setIsRedeemingCoupon(true);
    setErrorMessage('');

    try {
      const response = await redeemCouponCode(couponCode);
      setWallet((current) => current ? { ...current, freeReadings: response.freeReadings } : current);
      setCouponCode('');
      setNotice(
        response.alreadyClaimed
          ? `${getSpreadLabel(response.spread)} 무료 쿠폰이 이미 등록되어 있습니다.`
          : `${getSpreadLabel(response.spread)} 무료 쿠폰이 등록되었습니다. 이제 1회 무료로 리딩을 열 수 있습니다.`
      );
    } catch (error) {
      const apiError = error as Error;
      setErrorMessage(apiError.message || '쿠폰 등록에 실패했습니다.');
    } finally {
      setIsRedeemingCoupon(false);
    }
  };

  useEffect(() => {
    if (!recharged || !wallet?.packages?.length) return;

    const rechargedPackage = wallet.packages.find((entry) => entry.id === rechargedPackageId);
    const baseNotice = rechargedPackage
      ? `${rechargedPackage.label} 충전이 완료되었습니다.`
      : '츄르 충전이 완료되었습니다.';

    if (intent && balancePoints >= intent.requiredPoints) {
      setNotice(`${baseNotice} 이제 바로 이어서 리딩을 열 수 있습니다.`);
      return;
    }

    setNotice(baseNotice);
  }, [balancePoints, intent, recharged, rechargedPackageId, wallet?.packages]);

  const handleStartRecharge = async () => {
    if (!selectedPackage || isRecharging) return;

    trackEvent('payment_started', {
      source: 'wallet_recharge',
      package_id: selectedPackage.id,
      amount_krw: selectedPackage.amountKrw,
      points: selectedPackage.points,
      spread: intent?.spread ?? '',
    });

    setIsRecharging(true);
    setErrorMessage('');

    try {
      if (isMockKakaoFlow) {
        const nextParams = new URLSearchParams({
          recharged: '1',
          packageId: selectedPackage.id,
        });
        const mockParams = new URLSearchParams({
          provider: 'kakaopay-demo',
          mode: 'wallet-recharge',
          packageId: selectedPackage.id,
          amount: String(selectedPackage.amountKrw),
          points: `${selectedPackage.points}츄르`,
          label: selectedPackage.label,
          next: `/wallet/recharge?${nextParams.toString()}`,
        });

        navigate(`/payment/mock?${mockParams.toString()}`);
        return;
      }

      const ready = await startKakaoPayReady({
        orderType: 'wallet-recharge',
        packageId: selectedPackage.id,
        readingId: intent?.type === 'reading' ? intent.readingId : undefined,
        spread: intent?.type === 'reading' ? intent.spread : undefined,
      });

      const pendingCheckoutState: PendingKakaoPayState = {
        orderType: 'wallet-recharge',
        packageId: selectedPackage.id,
        readingId: intent?.type === 'reading' ? intent.readingId : undefined,
        orderId: ready.orderId,
        tid: ready.tid,
        amount: selectedPackage.amountKrw,
        spread: intent?.type === 'reading' ? intent.spread : undefined,
      };

      window.sessionStorage.setItem(KAKAOPAY_PENDING_KEY, JSON.stringify(pendingCheckoutState));

      const isMobile =
        /Mobi|Android|iPhone|iPad/i.test(window.navigator.userAgent) || window.innerWidth < 768;
      const redirectUrl = isMobile
        ? ready.nextRedirectMobileUrl || ready.nextRedirectPcUrl || ready.nextRedirectAppUrl
        : ready.nextRedirectPcUrl || ready.nextRedirectMobileUrl || ready.nextRedirectAppUrl;

      if (!redirectUrl) {
        throw new Error('카카오페이 이동 주소를 받지 못했습니다.');
      }

      window.location.assign(redirectUrl);
    } catch (error) {
      const apiError = error as Error;
      setErrorMessage(apiError.message || '카카오페이 충전 준비에 실패했습니다.');
      setIsRecharging(false);
    }
  };

  const handleContinueSelection = () => {
    if (!intent || intent.type !== 'selection') return;

    persistReadingSelection(intent.spread, intent.topic, intent.question);
    clearWalletRechargeIntent();
    navigate(getSelectionRoute(intent.spread, intent.topic));
  };

  const handleOpenReading = async () => {
    if (!intent || intent.type !== 'reading' || isResuming) return;

    setIsResuming(true);
    setErrorMessage('');

    try {
      window.sessionStorage.setItem(`reading_${intent.readingId}`, intent.replayToken);
      await spendWalletForReading(intent.readingId, intent.spread);
      window.sessionStorage.removeItem(PENDING_READING_KEY);
      clearWalletRechargeIntent();
      navigate(`/spread-layout/${intent.readingId}`);
    } catch (error) {
      const apiError = error as Error;
      setErrorMessage(apiError.message || '리딩을 다시 여는 중 문제가 발생했습니다.');
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#110d0c] px-4 py-12 text-[#f7ecde] sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-5xl">
        <div className="rounded-[34px] border border-[#7d5528]/45 bg-[radial-gradient(circle_at_top,rgba(120,76,32,0.14),rgba(20,14,11,0.97)_68%)] p-6 shadow-[0_35px_100px_-45px_rgba(0,0,0,0.95)] sm:p-8">
          <div className="text-center">
            <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89f6b]">Churu Wallet</p>
            <h1 className="mt-4 font-serif text-4xl font-extrabold tracking-[-0.02em] text-[#fbf2e4] md:text-5xl">츄르를 충전하세요</h1>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-[#dfc4a4]/82 md:text-base">
              묘안 리딩은 츄르로 엽니다. 100원 = 1츄르 기준으로 필요한 만큼만 채우고, 바로 이어갈 수 있습니다.
            </p>
          </div>

          {!user && !isLoading ? (
            <div className="mx-auto mt-10 max-w-xl rounded-[28px] border border-[#704f31]/45 bg-[#16100d]/88 p-6 text-center">
              <p className="text-lg font-semibold text-[#f7e7c9]">먼저 로그인해 주세요.</p>
              <p className="mt-3 text-sm leading-7 text-[#dbc0a0]/84">
                츄르 지갑은 계정에 보관됩니다. 로그인하면 지금 가진 양을 확인하고 충전할 수 있습니다.
              </p>
              <button
                type="button"
                onClick={handleLogin}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-[#b8842f]/55 bg-[#251811] px-6 py-3 text-sm font-semibold text-[#f7d48a] transition-colors hover:bg-[#311f15]"
              >
                <UiIcon name="arrow-right" className="h-4 w-4" />
                Google 로그인
              </button>
            </div>
          ) : (
            <>
              <div className="mt-10 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="rounded-[28px] border border-[#6e5238]/38 bg-[#16100d]/85 p-5 sm:p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.28em] text-[#b48964]">현재 지갑</p>
                      <p className="mt-2 text-3xl font-semibold text-[#f7ead5]">
                        {isLoading ? '...' : `${balancePoints}츄르`}
                      </p>
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#7b5d43]/55 bg-[#211813] text-[#f2cb84]">
                      <UiIcon name="coin" className="h-5 w-5" />
                    </div>
                  </div>

                  {intent ? (
                    <div className="mt-5 rounded-[22px] border border-[#775634]/45 bg-[#1d1511]/90 px-4 py-4">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#c39a68]">이어질 세션</p>
                      <p className="mt-2 text-base font-semibold text-[#f7ead5]">
                        {intent.type === 'selection' ? intent.question : '방금 고른 카드 리딩'}
                      </p>
                      <p className="mt-2 text-sm leading-7 text-[#dcc2a4]/84">
                        이번 세션을 열려면 {intent.requiredPoints}츄르가 필요합니다.
                      </p>
                    </div>
                  ) : (
                    <div className="mt-5 rounded-[22px] border border-[#775634]/45 bg-[#1d1511]/90 px-4 py-4">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#c39a68]">자유 충전</p>
                      <p className="mt-2 text-sm leading-7 text-[#dcc2a4]/84">
                        지금은 특정 리딩과 연결되지 않은 상태입니다. 필요한 만큼 츄르를 미리 충전해 둘 수 있습니다.
                      </p>
                    </div>
                  )}

                  {notice && (
                    <div className="mt-4 rounded-[18px] border border-[#5c7b48]/45 bg-[#182114]/90 px-4 py-3 text-sm leading-6 text-[#dff0c7]">
                      {notice}
                    </div>
                  )}

                  {errorMessage && (
                    <div className="mt-4 rounded-[18px] border border-[#7b342f]/45 bg-[#2b1513] px-4 py-3 text-sm leading-6 text-[#ffd7d2]">
                      {errorMessage}
                    </div>
                  )}

                  <div className="mt-5 rounded-[22px] border border-[#775634]/45 bg-[#1d1511]/90 px-4 py-4">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#c39a68]">무료 리딩 쿠폰</p>
                    <p className="mt-2 text-sm leading-7 text-[#dcc2a4]/84">
                      쿠폰 코드를 등록하면 해당 스프레드 리딩을 1회 무료로 사용할 수 있습니다.
                    </p>
                    <div className="mt-4 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                      <input
                        value={couponCode}
                        onChange={(event) => setCouponCode(event.target.value.toUpperCase())}
                        placeholder="쿠폰 코드 입력"
                        className="border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          void handleRedeemCoupon();
                        }}
                        disabled={!couponCode.trim() || isRedeemingCoupon}
                        className="inline-flex items-center justify-center gap-2 border border-[#8a6a4b]/45 bg-[#241913] px-4 py-3 text-sm font-medium text-[#f3dfc5] hover:bg-[#2d2019] disabled:opacity-60"
                      >
                        <UiIcon name="check" className="h-4 w-4" />
                        {isRedeemingCoupon ? '등록 중...' : '쿠폰 등록'}
                      </button>
                    </div>
                    <div className="mt-4 grid gap-2 sm:grid-cols-3">
                      <div className="border border-[#8a6a4b]/25 bg-black/15 px-3 py-3 text-sm text-[#d8bfa1]/80">
                        1카드: <span className="font-semibold text-[#f7d48a]">{freeReadings['one-card']}개</span>
                      </div>
                      <div className="border border-[#8a6a4b]/25 bg-black/15 px-3 py-3 text-sm text-[#d8bfa1]/80">
                        3카드: <span className="font-semibold text-[#f7d48a]">{freeReadings['three-card']}개</span>
                      </div>
                      <div className="border border-[#8a6a4b]/25 bg-black/15 px-3 py-3 text-sm text-[#d8bfa1]/80">
                        10카드: <span className="font-semibold text-[#f7d48a]">{freeReadings['celtic-cross']}개</span>
                      </div>
                    </div>
                  </div>

                  {intent && canContinueWithIntent && (
                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                      {intent.type === 'selection' ? (
                        <button
                          type="button"
                          onClick={handleContinueSelection}
                          className="inline-flex items-center justify-center gap-2 rounded-full border border-[#b8842f]/55 bg-[#2a1c13] px-5 py-3 text-sm font-semibold text-[#f7d48a] transition-colors hover:bg-[#322117]"
                        >
                          <UiIcon name="sparkles" className="h-4 w-4" />
                          카드 선택 이어가기
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            void handleOpenReading();
                          }}
                          disabled={isResuming}
                          className="inline-flex items-center justify-center gap-2 rounded-full border border-[#b8842f]/55 bg-[#2a1c13] px-5 py-3 text-sm font-semibold text-[#f7d48a] transition-colors hover:bg-[#322117] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <UiIcon name="sparkles" className="h-4 w-4" />
                          {isResuming ? '리딩 여는 중...' : '지금 리딩 열기'}
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => navigate('/spreads')}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#765637]/45 bg-[#17120f] px-5 py-3 text-sm font-semibold text-[#f4dfc0] transition-colors hover:bg-[#201712]"
                      >
                        새 스프레드 보러 가기
                      </button>
                    </div>
                  )}
                </div>

                <div className="rounded-[28px] border border-[#6e5238]/38 bg-[#16100d]/85 p-5 sm:p-6">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-[#b48964]">결제 준비 상태</p>
                  <p className="mt-3 text-sm leading-7 text-[#dbc1a2]/82">
                    츄르 충전은 카카오페이로 진행됩니다. 결제를 마치면 다시 묘안으로 돌아와 충전 결과가 반영됩니다.
                  </p>

                  <div className="mt-5 rounded-[22px] border border-[#7c642d]/55 bg-[#f7d44f] px-5 py-4 text-[#2a2111]">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#20180a] text-[#f7d44f]">
                          <UiIcon name="coin" className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.22em] text-[#4a3815]">결제 수단</p>
                          <p className="mt-1 text-base font-semibold">카카오페이</p>
                        </div>
                      </div>
                      <span className="rounded-full bg-white/40 px-3 py-1 text-xs font-semibold text-[#3b2d0c]">
                        {isMockKakaoFlow ? '데모 결제' : providerReady && provider === 'kakaopay' ? '준비 완료' : '설정 확인 중'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 grid gap-4 lg:grid-cols-3">
                {wallet?.packages.map((entry) => {
                  const isSelected = entry.id === selectedPackageId;

                  return (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => setSelectedPackageId(entry.id)}
                      className={`relative rounded-[28px] border p-5 text-left transition-all duration-200 ${
                        isSelected
                          ? 'border-[#d39a32] bg-[#221712] shadow-[0_20px_45px_-28px_rgba(251,191,36,0.45)]'
                          : 'border-[#6e5238]/38 bg-[#16100d]/85 hover:border-[#9b7332]'
                      }`}
                    >
                      {entry.recommended && (
                        <div className="absolute right-4 top-4 rounded-full border border-[#88652b]/55 bg-[#22160e] px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-[#f2c56b]">
                          추천
                        </div>
                      )}
                      <p className="text-[11px] uppercase tracking-[0.28em] text-[#b48964]">충전 패키지</p>
                      <p className="mt-3 text-3xl font-semibold text-[#f7ead5]">{entry.label}</p>
                      <p className="mt-3 text-sm leading-7 text-[#dbc1a2]/82">{entry.description}</p>
                      <div className="mt-6 flex items-end justify-between gap-4">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.18em] text-[#a98762]">결제 금액</p>
                          <p className="mt-2 text-2xl font-semibold text-[#f7d48a]">{entry.amountKrw.toLocaleString()}원</p>
                        </div>
                        <p className="text-xs text-[#d5b998]/76">충전 후 {balancePoints + entry.points}츄르</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm leading-7 text-[#dbc1a2]/84">
                  {selectedPackage ? (
                    <>
                      지금 <span className="font-semibold text-[#f7ead5]">{selectedPackage.label}</span>를 고르면 충전 후{' '}
                      <span className="font-semibold text-[#f7d48a]">{projectedBalance}츄르</span>가 됩니다.
                    </>
                  ) : (
                    '충전 패키지를 고르면 예상 보유량을 바로 확인할 수 있습니다.'
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    void handleStartRecharge();
                  }}
                  disabled={!selectedPackage || isRecharging || isLoading || !user || !canStartKakaoRecharge}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#b8842f]/55 bg-[#2a1c13] px-6 py-3 text-sm font-semibold text-[#f7d48a] transition-colors hover:bg-[#322117] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <UiIcon name="coin" className="h-4 w-4" />
                  {isRecharging ? '카카오페이로 이동하는 중...' : '카카오페이로 츄르 충전하기'}
                </button>
              </div>

              {paymentMode === 'disabled' && (
                <div className="mt-4 rounded-[18px] border border-[#7b342f]/45 bg-[#2b1513] px-4 py-3 text-sm leading-6 text-[#ffd7d2]">
                  현재는 결제를 받을 수 없는 설정입니다. 잠시 후 다시 시도해 주세요.
                </div>
              )}

              {!isMockKakaoFlow && paymentMode !== 'disabled' && (provider !== 'kakaopay' || !providerReady) && (
                <div className="mt-4 rounded-[18px] border border-[#7b342f]/45 bg-[#2b1513] px-4 py-3 text-sm leading-6 text-[#ffd7d2]">
                  카카오페이 설정이 아직 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.
                </div>
              )}

              <CheckoutComplianceNotice className="mt-6" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
